const courseDetails = {
  'ig-wa-commerce': {
    overview:
      'Learn a practical framework for writing prompts that make AI tools produce clear, accurate, and useful outputs for work, business, and daily execution.',
    outcomes: [
      'Write prompts that reduce vague or low quality AI responses',
      'Create reusable prompt structures for content, research, and planning',
      'Use role, context, constraints, and examples to improve output quality',
      'Review and refine AI responses faster with a simple iteration process',
    ],
    modules: [
      {
        title: 'Module 1: Prompting Fundamentals',
        topics: [
          'How AI tools interpret prompts',
          'Prompt structure that improves consistency',
          'Common mistakes and how to avoid them',
        ],
      },
      {
        title: 'Module 2: Practical Prompt Frameworks',
        topics: [
          'Prompts for content, research, and ideation',
          'Role + context + constraint method',
          'Template building for repeatable outcomes',
        ],
      },
      {
        title: 'Module 3: Review and Optimization',
        topics: [
          'How to evaluate AI output quality',
          'Refinement loops for better results',
          'Adapting one prompt for multiple use cases',
        ],
      },
    ],
    whoItsFor: [
      'Founders and business owners using AI to speed up execution',
      'Creators and marketers who need better copy and content ideas',
      'Professionals who want reliable AI support in everyday tasks',
      'Beginners who want a clear process instead of random prompting',
    ],
    courseFormat: [
      'Step by step video lessons',
      'Prompt templates you can adapt immediately',
      'Real examples from business and productivity use cases',
    ],
    delivery: {
      mode: 'Live Zoom classes',
      note: 'All teaching sessions for this course are delivered via Zoom.',
      support: 'Class recordings and support resources are shared after each session.',
    },
    callout:
      'By the end of this course, you will have a repeatable prompting workflow you can apply across any modern AI tool.',
  },
  'data-analytics': {
    overview:
      'Master practical data analytics skills using Excel and Power BI so you can turn business data into clear decisions, better reporting, and measurable growth.',
    outcomes: [
      'Clean and structure raw data for analysis in Excel',
      'Build dashboards in Power BI that decision makers can use quickly',
      'Use key metrics to evaluate performance and identify opportunities',
      'Present insights clearly to teams, clients, and stakeholders',
    ],
    modules: [
      {
        title: 'Module 1: Data Foundations in Excel',
        topics: [
          'Working with datasets, tables, and data types',
          'Cleaning data with formulas and validation checks',
          'Using pivot tables and summaries for quick insights',
        ],
      },
      {
        title: 'Module 2: Business Analysis and Reporting',
        topics: [
          'Tracking KPIs for sales, operations, and growth',
          'Building analysis models for decision support',
          'Creating professional reports for non-technical audiences',
        ],
      },
      {
        title: 'Module 3: Power BI Dashboards',
        topics: [
          'Importing and modeling data in Power BI',
          'Designing interactive visual dashboards',
          'Publishing and sharing reports for team use',
        ],
      },
    ],
    whoItsFor: [
      'Business owners who want to make smarter decisions from data',
      'Professionals transitioning into analytics or reporting roles',
      'Operations and finance teams that need cleaner performance tracking',
      'Beginners who want practical analytics skills with real business context',
    ],
    courseFormat: [
      'Live Zoom sessions with guided walkthroughs',
      'Hands-on assignments using Excel and Power BI',
      'Templates and dashboard examples for real business use',
    ],
    delivery: {
      mode: 'Live Zoom classes',
      note: 'This course is delivered through instructor-led Zoom classes.',
      support: 'Session recordings, practice files, and support materials are provided after each class.',
    },
    callout:
      'By the end of this course, you will be able to convert raw business data into actionable insights and polished dashboards.',
  },
  'ai-nocode': {
    overview:
      'Learn how to combine AI tools and no-code platforms to automate repetitive business tasks, reduce manual work, and improve team efficiency without writing code.',
    outcomes: [
      'Identify high impact workflows to automate in your business',
      'Design no-code automations for operations, marketing, and reporting',
      'Integrate AI into workflows for smarter content, support, and decision making',
      'Launch reliable automation systems with monitoring and fallback steps',
    ],
    modules: [
      {
        title: 'Module 1: Automation Strategy for Business',
        topics: [
          'Finding time-consuming tasks worth automating',
          'Mapping workflows and defining success metrics',
          'Choosing the right no-code and AI tool stack',
        ],
      },
      {
        title: 'Module 2: Building No-Code Workflows',
        topics: [
          'Creating trigger-action automations across tools',
          'Automating lead capture, follow-up, and onboarding',
          'Building internal task and notification systems',
        ],
      },
      {
        title: 'Module 3: AI-Powered Business Systems',
        topics: [
          'Using AI for content drafts and response workflows',
          'Automating analytics summaries and decision reports',
          'Testing, improving, and scaling automation safely',
        ],
      },
    ],
    whoItsFor: [
      'Business owners who want lean teams and faster execution',
      'Operations managers improving process efficiency',
      'Marketers and creators automating repetitive campaign work',
      'Professionals who want practical AI + no-code implementation skills',
    ],
    courseFormat: [
      'Live Zoom classes with implementation walkthroughs',
      'Hands-on workflow build sessions and templates',
      'Real business automation case studies and examples',
    ],
    delivery: {
      mode: 'Live Zoom classes',
      note: 'This course is delivered through live Zoom sessions with practical build-along exercises.',
      support: 'Recordings, workflow templates, and implementation guides are shared after each class.',
    },
    callout:
      'By the end of this course, you will have deployable AI and no-code automation systems running in your business workflows.',
  },
  'aws-cloud-foundations-beginner': {
    overview:
      'Start your AWS journey with a beginner-friendly, practical program that teaches core cloud concepts, AWS services, and real deployment workflows you can use in entry-level cloud roles.',
    outcomes: [
      'Understand core cloud concepts and AWS global infrastructure',
      'Launch and manage foundational AWS services with confidence',
      'Build secure and cost-aware cloud environments',
      'Prepare for AWS Cloud Foundations level responsibilities and interviews',
    ],
    modules: [
      {
        title: 'Module 1: Cloud Fundamentals and AWS Basics',
        topics: [
          'Cloud computing models and shared responsibility',
          'AWS regions, availability zones, and core services',
          'Creating and navigating your AWS account setup',
        ],
      },
      {
        title: 'Module 2: Core AWS Services Hands-On',
        topics: [
          'Compute with EC2 and storage with S3',
          'Identity and access management with IAM',
          'Networking basics with VPC and security groups',
        ],
      },
      {
        title: 'Module 3: Security, Cost, and Best Practices',
        topics: [
          'Foundational security controls and monitoring',
          'Cost estimation and optimization basics',
          'Real-world deployment workflow for beginners',
        ],
      },
    ],
    whoItsFor: [
      'Beginners starting a cloud or DevOps career path',
      'Tech professionals moving into AWS-focused roles',
      'Developers and IT support staff building cloud fundamentals',
      'Students preparing for early-stage AWS certifications',
    ],
    courseFormat: [
      'Live Zoom classes with instructor-led demos',
      'Guided AWS hands-on labs and practice activities',
      'Class notes, checklists, and implementation resources',
    ],
    delivery: {
      mode: 'Live Zoom classes',
      note: 'This course is delivered through live Zoom sessions with practical AWS walkthroughs.',
      support: 'Recordings and downloadable resources are shared after each class.',
    },
    callout:
      'By the end of this course, you will have the confidence and practical foundation to start building with AWS in real-world scenarios.',
  },
  'aws-solutions-architect-professional': {
    overview:
      'Advance from foundational AWS skills to professional-level architecture by learning how to design secure, highly available, and cost-efficient systems for complex enterprise workloads.',
    outcomes: [
      'Design fault-tolerant and multi-account AWS architectures',
      'Apply advanced security, governance, and compliance controls',
      'Select the right data, compute, and networking services for scale',
      'Prepare confidently for senior cloud architecture responsibilities',
    ],
    modules: [
      {
        title: 'Module 1: Advanced Architecture Principles',
        topics: [
          'Well-Architected design decisions at professional level',
          'Multi-account strategy and AWS Organizations',
          'Tradeoff analysis for resilience, performance, and cost',
        ],
      },
      {
        title: 'Module 2: Networking, Security, and Governance',
        topics: [
          'Hybrid and multi-region networking patterns',
          'Advanced IAM strategy, KMS, and security controls',
          'Policy-based governance and operational guardrails',
        ],
      },
      {
        title: 'Module 3: Data, Migration, and Optimization',
        topics: [
          'Designing data platforms for scale and reliability',
          'Migration strategies for legacy and cloud-native systems',
          'Performance tuning, observability, and cost optimization',
        ],
      },
    ],
    whoItsFor: [
      'Cloud engineers moving into architecture-focused roles',
      'Solutions architects preparing for complex AWS deployments',
      'Senior developers designing enterprise cloud systems',
      'Professionals targeting advanced AWS certification pathways',
    ],
    courseFormat: [
      'Live Zoom classes with advanced architecture walkthroughs',
      'Case-study driven sessions and design exercises',
      'Architecture templates and implementation guides',
    ],
    delivery: {
      mode: 'Live Zoom classes',
      note: 'This course is delivered via live Zoom sessions focused on professional architecture scenarios.',
      support: 'Session recordings and architecture resources are shared after each class.',
    },
    callout:
      'By the end of this course, you will be able to architect production-ready AWS solutions for high-scale business environments.',
  },
  'ai-prompting-business-workflows': {
    overview:
      'Learn how to embed AI prompting into day-to-day business workflows so your team can produce better outputs faster across operations, sales, marketing, and decision support.',
    outcomes: [
      'Build repeatable prompt systems for common business tasks',
      'Improve output quality with structured prompt frameworks',
      'Reduce turnaround time for content, analysis, and planning',
      'Create team-ready prompt libraries for consistent execution',
    ],
    modules: [
      {
        title: 'Module 1: Prompting Strategy for Business Teams',
        topics: [
          'Identifying workflows where prompting creates the most value',
          'Prompt structure for clarity, context, and constraints',
          'Setting quality standards for AI-assisted outputs',
        ],
      },
      {
        title: 'Module 2: Prompting Across Core Workflows',
        topics: [
          'Marketing and content prompts for campaign execution',
          'Sales and customer communication prompt templates',
          'Operations and reporting prompts for faster decisions',
        ],
      },
      {
        title: 'Module 3: Team Adoption and Optimization',
        topics: [
          'Building reusable prompt libraries and SOPs',
          'Review loops to improve prompt performance over time',
          'Governance practices for safe and consistent AI usage',
        ],
      },
    ],
    whoItsFor: [
      'Business owners implementing AI in daily operations',
      'Team leads improving productivity with practical AI workflows',
      'Marketing, sales, and operations professionals',
      'Professionals who want structured prompting beyond one-off usage',
    ],
    courseFormat: [
      'Live Zoom classes with real workflow examples',
      'Hands-on exercises and reusable prompt templates',
      'Implementation guides for immediate team adoption',
    ],
    delivery: {
      mode: 'Live Zoom classes',
      note: 'This course is delivered through live Zoom sessions focused on real business workflow use cases.',
      support: 'Session recordings, template packs, and support resources are shared after each class.',
    },
    callout:
      'By the end of this course, you will have a practical prompting system your business can apply consistently across multiple workflows.',
  },
};

export default courseDetails;
